import { WABATemplateCategory, WABATemplateQuality, WABATemplateStatus } from "@/@integrations/WABA/protocols/wabaChannelMessageTemplate"
import WhatsappBackground from "@/assets/images/waba/whatsapp_background.png"
import { Notification, PopConfirm, Portlet } from "@/components"
import WabaTemplateGroupPermissionGate from "@/components/PermissionGate/WabaTemplateGroupPermissionGate"
import MessageItem from "@/components/ChatMessageBuilder/MessageItem"
import { TEMPLATE_QUALITY_TO_READABLE_QUALITY, TEMPLATE_STATUS_TO_READABLE_STATUS } from "@/pages/Admin/Templates"
import useStyles from "@/pages/Admin/Templates/components/TemplateCard/styles"
import useTemplateErrorTranslation from "@/pages/Admin/Templates/hooks/useTemplateErrorTranslation"
import { BuildedMessage } from "@/protocols/messages"
import ApiService from "@/services/Api"
import { useGlobalStateStore } from "@/store/GlobalState"
import {
	CardActions,
	CardContent,
	CardHeader,
	Chip,
	CircularProgress,
	Grid,
	IconButton,
	Menu,
	MenuItem,
	Tooltip,
	Typography
} from "@material-ui/core"
import {
	MoreVert as MoreVertIcon,
	InfoOutlined as InfoIcon
} from "@material-ui/icons"
import React, { useState } from "react"
import { useHistory } from "react-router-dom"

type TemplateCardProps = {
	id: number
	name: string
	status: WABATemplateStatus
	quality: WABATemplateQuality
	category: WABATemplateCategory
	message: BuildedMessage
	errorReason?: string
	onDelete: (templateId: number) => void
	onEdit?: () => void
}

const TemplateCard: React.FC<TemplateCardProps> = (props) => {
	const {
		id,
		category,
		message,
		name,
		quality,
		status,
		errorReason,
		onDelete
	} = props
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
	const open = Boolean(anchorEl)
	const globalStateStore = useGlobalStateStore()
	const history = useHistory()

	const hasValidError = status === "failed" && Boolean(errorReason)

	const { translatedError, isLoading: isLoadingTranslation } = useTemplateErrorTranslation({
		templateId: id,
		enabled: hasValidError
	})

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget)
	}

	const handleMenuClose = () => {
		setAnchorEl(null)
	}

	const confirmDelete = () => {
		PopConfirm.open({
			title: "EXCLUIR TEMPLATE?",
			description: "Tem certeza de que deseja continuar? Essa ação é irreversível e também removerá o template da plataforma GupShup.",
			onConfirm: async () => {
				try {
					await ApiService.delete(`/waba/channel/${globalStateStore.currentChannel?.id}/message/template/${name}`, {
						params: {
							id: id
						}
					})
					onDelete(id)
				} catch (error) {
					Notification.error({
						message: "Ocorreu um erro inesperado ao excluir o template. Por favor tente novamente."
					})
				}
			},
			confirmButtonText: "Excluir"
		})
	}

	const onEdit = () => {
		history.push("/admin/template/manage", {
			isEditMode: true,
			id
		})
	}

	const onDuplicate = () => {
		history.push("/admin/template/manage", {
			id,
			isDuplicateMode: true
		})
	}

	const templateCategoryToReadableCategory: Record<WABATemplateCategory, string> = {
		authentication: "Autenticação",
		marketing: "Marketing",
		otp: "Otp",
		utility: "Utilidade"
	}

	const classes = useStyles({
		qualityBackgroundColor: TEMPLATE_QUALITY_TO_READABLE_QUALITY[quality].color,
		statusBackgroundColor: TEMPLATE_STATUS_TO_READABLE_STATUS[status].color
	})

	const errorTooltipTitle = isLoadingTranslation
		? <CircularProgress size={12} color="inherit" />
		: (translatedError || errorReason) as string

	return (
		<Grid item>
			<Portlet
				style={{
					width: 330
				}}>
				<Grid container spacing={1}>
					<Grid item xs={12}>
						<CardHeader
							title={<Tooltip
								title={name}
							>
								<Typography className={classes.name}>{name}</Typography>
							</Tooltip>}
							action={
								<IconButton onClick={handleMenuOpen} className={classes.moreOptionsButton}>
									<MoreVertIcon />
								</IconButton>
							}
							className={classes.header}
						/>
					</Grid>
					<Grid item xs={12} className={classes.feedbackGrid}>
						{ status === "failed"
							? <Tooltip
								title={errorTooltipTitle}
								disableHoverListener={!hasValidError}
							>
								<Chip
									label={
										<span className={classes.chipLabel}>
											{TEMPLATE_STATUS_TO_READABLE_STATUS[status].label}
											{hasValidError && <InfoIcon className={classes.iconStyle} />}
										</span>
									}
									className={classes.statusChip}
								/>
							</Tooltip>
							: <Tooltip
								title={TEMPLATE_STATUS_TO_READABLE_STATUS[status]?.description || ""}
								disableHoverListener={!TEMPLATE_STATUS_TO_READABLE_STATUS[status]?.description}
							>
								<Chip label={TEMPLATE_STATUS_TO_READABLE_STATUS[status].label} className={classes.statusChip} />
							</Tooltip>}
						<Tooltip
							title={TEMPLATE_QUALITY_TO_READABLE_QUALITY[quality]?.description || ""}
							disableHoverListener={!TEMPLATE_QUALITY_TO_READABLE_QUALITY[quality]?.description}
						>
							<Chip label={TEMPLATE_QUALITY_TO_READABLE_QUALITY[quality].label} className={classes.qualityChip} />
						</Tooltip>
					</Grid>
					<Grid item xs={12}>
						<CardContent
							className={classes.content}
							style={{
								backgroundImage: `url(${WhatsappBackground})`
							}}>
							<MessageItem
								type={message.type}
								content={message.content}
								mediaName={message?.mediaName}
								extraData={message?.extraData}
								isDefaultSize={false}
								copySpecial={false}
							/>
						</CardContent>
					</Grid>
					<Grid item xs={12}>
						<CardActions className={classes.footer}>
							<Chip label={templateCategoryToReadableCategory[category]} className={classes.categoryChip} />
						</CardActions>
					</Grid>
				</Grid>
				<Menu
					anchorEl={anchorEl}
					open={open}
					onClose={handleMenuClose}
				>
					{status === "failed"
						? <Tooltip
							title={"A duplicação de templates de mídia não está disponível pela Letalk."}
							disableHoverListener={message.type === "text"}
						>
							<MenuItem onClick={onDuplicate}>Duplicar</MenuItem>
						</Tooltip>
						: <WabaTemplateGroupPermissionGate action="update">
							<Tooltip
								title={"A edição de templates de mídia não está disponível pela Letalk."}
								disableHoverListener={message.type === "text"}
							>
								<MenuItem onClick={onEdit}>Editar</MenuItem>
							</Tooltip>
						</WabaTemplateGroupPermissionGate>
					}
					<WabaTemplateGroupPermissionGate action="delete">
						<MenuItem onClick={confirmDelete}>Excluir</MenuItem>
					</WabaTemplateGroupPermissionGate>
				</Menu>
			</Portlet>
		</Grid>
	)
}

export default TemplateCard
